import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Heart, TrendingUp, TrendingDown, Minus, Sparkles } from "lucide-react";

/**
 * "How They're Feeling" card — CherishLink's emotional reassurance layer.
 *
 * Props:
 *   emotional: { mood_today, energy_level, connection_level, loneliness_signals,
 *                highlights[], suggested_action, call_date }
 *   trend: { status: 'improving'|'stable'|'declining'|'insufficient', avg_7d, avg_prev_7d, delta }
 *   lovedOneName: string
 */
export function FeelingCard({ emotional, trend, lovedOneName }) {
  if (!emotional) return null;

  const first = (lovedOneName || "").split(" ")[0] || "Your loved one";
  const {
    mood_today,
    energy_level,
    connection_level,
    loneliness_signals,
    highlights = [],
    suggested_action,
    call_date,
  } = emotional;

  const moodColor = moodToColor(mood_today);
  const lonelinessColor = lonelinessToColor(loneliness_signals);
  const trendMeta = trendToMeta(trend);

  return (
    <Card
      className="rounded-3xl border-border/60 soft-shadow bg-gradient-to-br from-white via-accent/20 to-white"
      data-testid="feeling-card"
    >
      <CardContent className="p-6 md:p-8">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Heart className="h-4 w-4 text-secondary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                How they're feeling
              </span>
            </div>
            <h3 className="mt-2 font-display text-xl font-semibold text-foreground">
              {first} seemed {(mood_today || "okay").toLowerCase()} today
            </h3>
          </div>
          <TrendBadge trendMeta={trendMeta} />
        </div>

        {/* Chip grid */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Chip label="Mood today" value={mood_today} color={moodColor} testid="feeling-mood" />
          <Chip label="Energy" value={energy_level} color="text-primary" testid="feeling-energy" />
          <Chip label="Connection" value={connection_level} color="text-foreground" testid="feeling-connection" />
          <Chip label="Loneliness" value={loneliness_signals} color={lonelinessColor} testid="feeling-loneliness" />
        </div>

        {/* Highlights */}
        {highlights.length > 0 && (
          <div className="mt-6" data-testid="feeling-highlights">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              What came up
            </div>
            <ul className="mt-2 space-y-1.5">
              {highlights.slice(0, 4).map((h, i) => (
                <li key={i} className="text-sm text-foreground flex items-start gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-secondary mt-2 shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Suggested action */}
        {suggested_action && (
          <div
            className="mt-6 rounded-2xl border border-primary/20 bg-primary/5 p-4 md:p-5"
            data-testid="feeling-action"
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Suggested next step
            </div>
            <p className="mt-2 text-sm md:text-base text-foreground leading-relaxed">
              {suggested_action}
            </p>
          </div>
        )}

        {call_date && (
          <p className="mt-4 text-xs text-muted-foreground">
            Based on the call from {new Date(call_date + "T12:00:00Z").toLocaleDateString([], { month: "short", day: "numeric" })}
            · Observations only — not medical advice.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Chip({ label, value, color, testid }) {
  if (!value) return null;
  return (
    <div className="rounded-2xl border border-border/60 bg-white px-4 py-3" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-sm md:text-base font-semibold ${color}`}>
        {value}
      </div>
    </div>
  );
}

function TrendBadge({ trendMeta }) {
  if (!trendMeta) return null;
  const { label, icon: Icon, tone } = trendMeta;
  return (
    <Badge
      className={`rounded-full px-3 py-1.5 text-xs font-semibold border-0 ${tone}`}
      data-testid="feeling-trend-badge"
    >
      <Icon className="h-3 w-3 mr-1 inline-block" />
      Trend: {label}
    </Badge>
  );
}

function moodToColor(mood) {
  const m = (mood || "").toLowerCase();
  if (m === "upbeat") return "text-secondary";
  if (m === "good") return "text-secondary";
  if (m === "low") return "text-amber-600";
  if (m === "concerned") return "text-rose-600";
  return "text-foreground";
}

function lonelinessToColor(level) {
  const l = (level || "").toLowerCase();
  if (l === "elevated") return "text-rose-600";
  if (l === "moderate") return "text-amber-600";
  return "text-secondary";
}

function trendToMeta(trend) {
  if (!trend) return null;
  const s = trend.status;
  if (s === "improving") return { label: "improving", icon: TrendingUp, tone: "bg-secondary/15 text-secondary" };
  if (s === "declining") return { label: "declining", icon: TrendingDown, tone: "bg-amber-100 text-amber-800" };
  if (s === "stable") return { label: "stable", icon: Minus, tone: "bg-muted text-muted-foreground" };
  return null;
}
